import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { ICEPACK_FORM_NAME, REQUEST_ERROR_MESSAGE, FIELDS_FOR_VALIDATE } from './constants';
import {
	getIcepacksRequest,
	getIcepacksFillersRequest,
	getIcepacksStatesRequest,
	getModesDictionaryRequest,
	getIcepackFilledInfoRequest,
	createIcepackFilledRequest,
	updateIcepackFilledRequest,
	deleteIcepackFilledRequest,
} from '../../api/requests';
import {
	GET_ICEPACKS,
	setIcepacksAction,
	setIsIcepacksLoadingAction,
	GET_ICEPACKS_FILLERS,
	setIcepacksFillersAction,
	setIsIcepacksFillersLoadingAction,
	GET_ICEPACKS_STATES,
	setIcepacksStatesAction,
	setIsIcepacksStatesLoadingAction,
	GET_MODES,
	setModesAction,
	setIsModesLoadingAction,
	GET_ICEPACK_FILLED_INFO,
	setIcepackFilledInfoAction,
	setIsIcepackFilledInfoLoadingAction,
	CREATE_ICEPACK_FILLED,
	setIsIcepackFilledCreatingAction,
	UPDATE_ICEPACK_FILLED,
	setIsIcepackFilledUpdatingAction,
	DELETE_ICEPACK_FILLED,
	setIsIcepackFilledDeletingAction,
} from './actions';
import { DISMISS_TIME } from '../../api/constants';
import { errorHandler } from '../../api/utils';
import { getDataForSaveSelector, getFormIsInvalidSelector } from './selectors';
import { transformToValidationErrors } from './utils';
import { getIcepacksFilledAction } from '../icepacks-filled/actions';

export function* getIcepacksSaga() {
	try {
		yield put(setIsIcepacksLoadingAction(true));

		const { data, message, toast } = yield call(getIcepacksRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setIcepacksAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepacksLoadingAction(false));
	}
}

export function* getIcepacksFillersSaga() {
	try {
		yield put(setIsIcepacksFillersLoadingAction(true));

		const { data, message, toast } = yield call(getIcepacksFillersRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setIcepacksFillersAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepacksFillersLoadingAction(false));
	}
}

export function* getIcepacksStatesSaga() {
	try {
		yield put(setIsIcepacksStatesLoadingAction(true));

		const { data, message, toast } = yield call(getIcepacksStatesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setIcepacksStatesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepacksStatesLoadingAction(false));
	}
}

export function* getModesSaga() {
	try {
		yield put(setIsModesLoadingAction(true));

		const { data, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setModesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesLoadingAction(false));
	}
}

export function* getIcepackFilledInfoSaga({ payload: id }) {
	try {
		yield put(setIsIcepackFilledInfoLoadingAction(true));

		const { data, message, toast } = yield call(getIcepackFilledInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setIcepackFilledInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackFilledInfoLoadingAction(false));
	}
}

export function* createIcepackFilledSaga({ payload: { redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackFilledCreatingAction(true));

		const dataForSave = yield select(getDataForSaveSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(createIcepackFilledRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			redirect();

			yield put(getIcepacksFilledAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(ICEPACK_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(ICEPACK_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackFilledCreatingAction(false));
	}
}

export function* updateIcepackFilledSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackFilledUpdatingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForSaveSelector());

		const { data, errors, message, toast } = yield call(updateIcepackFilledRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(setIcepackFilledInfoAction(data));

			redirect();

			yield put(getIcepacksFilledAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(ICEPACK_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(ICEPACK_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackFilledUpdatingAction(false));
	}
}

export function* deleteIcepackFilledSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsIcepackFilledDeletingAction(true));

		const { data, message, toast } = yield call(deleteIcepackFilledRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			redirect();

			yield put(getIcepacksFilledAction({ searchQuery, noLoading: true }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepackFilledDeletingAction(false));
	}
}

export default function* icepackFilledModalSaga() {
	yield takeEvery(GET_ICEPACKS, getIcepacksSaga);
	yield takeEvery(GET_ICEPACKS_FILLERS, getIcepacksFillersSaga);
	yield takeEvery(GET_ICEPACKS_STATES, getIcepacksStatesSaga);
	yield takeEvery(GET_MODES, getModesSaga);
	yield takeEvery(GET_ICEPACK_FILLED_INFO, getIcepackFilledInfoSaga);
	yield takeEvery(CREATE_ICEPACK_FILLED, createIcepackFilledSaga);
	yield takeEvery(UPDATE_ICEPACK_FILLED, updateIcepackFilledSaga);
	yield takeEvery(DELETE_ICEPACK_FILLED, deleteIcepackFilledSaga);
}
